// src/app/store/embeddings/embedding.effects.ts
import {inject, Injectable} from '@angular/core';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import * as EmbeddingActions from './embedding.actions';
import {catchError, map, mergeMap} from 'rxjs/operators';
import {of} from 'rxjs';
import {EmbeddingService} from './embedding.service';

@Injectable()
export class EmbeddingEffects {
  private readonly embeddingService: EmbeddingService = inject(EmbeddingService);
  private readonly actions$: Actions = inject(Actions);

  loadEmbeddingsForProject$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EmbeddingActions.loadEmbeddingsForProject),
      mergeMap(action =>
        this.embeddingService.getAllEmbeddingsForProject(action.projectId).pipe(
          map(embeddings =>
            EmbeddingActions.loadEmbeddingsForProjectSuccess({embeddings, projectId: action.projectId})
          ),
          catchError(error =>
            of(EmbeddingActions.loadEmbeddingsForProjectFailure({error}))
          )
        )
      )
    )
  );

  loadHasAllPaperEmbedding$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EmbeddingActions.loadHasAllPaperEmbedding),
      mergeMap(action =>
        this.embeddingService.hasAllPaperEmbedding(action.projectId, action.filter).pipe(
          map(paperHasEmbeddings =>
            EmbeddingActions.loadHasAllPaperEmbeddingSuccess({paperHasEmbeddings, projectId: action.projectId})
          ),
          catchError(error =>
            of(EmbeddingActions.loadHasAllPaperEmbeddingFailure({error}))
          )
        )
      )
    )
  );

  loadProjectHasEmbeddingOverview$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EmbeddingActions.loadProjectHasEmbeddingOverview),
      mergeMap(action =>
        this.embeddingService.projectEmbeddingOverview(action.projectId).pipe(
          map(result =>
            EmbeddingActions.loadProjectHasEmbeddingOverviewSuccess({result, projectId: action.projectId})
          ),
          catchError(error =>
            of(EmbeddingActions.loadProjectHasEmbeddingOverviewFailure({error}))
          )
        )
      )
    )
  );

  loadEmbeddingsForPaper$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EmbeddingActions.loadEmbeddingsForPaper),
      mergeMap(action =>
        this.embeddingService.getAllEmbeddingsForPaper(action.paperId).pipe(
          map(embeddings =>
            EmbeddingActions.loadEmbeddingsForPaperSuccess({embeddings, paperId: action.paperId})
          ),
          catchError(error =>
            of(EmbeddingActions.loadEmbeddingsForPaperFailure({error}))
          )
        )
      )
    )
  );

  deleteEmbeddingsForPaper$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EmbeddingActions.deleteEmbeddingsForUser),
      mergeMap(action =>
        this.embeddingService.deleteUserEmbedding().pipe(
          map(() =>
            EmbeddingActions.deleteEmbeddingsForUserSuccess()
          ),
          catchError(error =>
            of(EmbeddingActions.deleteEmbeddingsForUserFailure({error}))
          )
        )
      )
    )
  );


  loadEmbeddingAugmentorResult$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EmbeddingActions.loadEmbeddingAugmentorResult),
      mergeMap(action =>
        this.embeddingService.queryEmbedding(action.projectId, action.query).pipe(
          map(results =>
            EmbeddingActions.loadEmbeddingAugmentorResultSuccess({results})
          ),
          catchError(error =>
            of(EmbeddingActions.loadEmbeddingAugmentorResultFailure({error}))
          )
        )
      )
    )
  );

  checkPaperEmbedding$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EmbeddingActions.checkAndCreatePaperEmbedding),
      mergeMap(action =>
        this.embeddingService.checkAndCreatePaperEmbedding(action.paperId).pipe(
          map(hasEmbedding =>
            EmbeddingActions.checkAndCreatePaperEmbeddingSuccess({hasEmbedding, paperId: action.paperId})
          ),
          catchError(error =>
            of(EmbeddingActions.checkAndCreatePaperEmbeddingFailure({error, paperId: action.paperId}))
          )
        )
      )
    )
  );
}
